import { FC, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  MenuItem,
  Autocomplete,
  IconButton,
  Divider,
  Link,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon,
  NoteAdd as NoteAddIcon,
  Assignment as SoapIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { todayLocalISO, ymdToNoonUtcIso } from '../../utils/dateUtils';
import { useFormatInstant } from '../../hooks/useClinicTimezone';
import api, { appointmentApi, AppointmentListItem, medicalRecordsApi } from '../../utils/api';
import { formatDollars, roundDollars } from '../../utils/money';
import { formatQuantity, parseQuantity } from '../../utils/quantity';
import QuantityField from '../../components/QuantityField';
import { barnsService, BarnRosterHorse } from '../../services/barnsService';
import { serviceCatalogService, ServiceCatalogItem } from '../../services/serviceCatalogService';
import { OfflineNotice, useIsOffline } from '../../offline';
import VisitNoteDialog from '../../components/VisitNote/VisitNoteDialog';
import SoapNoteDialog from '../../components/SoapNote/SoapNoteDialog';
import { useRBAC } from '../../auth/useRBAC';
import { barnVisitControls } from '../../auth/pageControls';

type ServiceType = 'procedure' | 'vaccination' | 'medication';

interface WorkItem {
  serviceType: ServiceType;
  serviceName: string;
  unitPrice: number;
  quantity?: number;
  notes?: string;
}

interface ClientEstimate {
  clientId: string;
  name: string;
  horseNames: string[];
  estimated: number;
}

const emptyDraft: { serviceType: ServiceType; serviceName: string; unitPrice: string; quantity: string } = {
  serviceType: 'vaccination',
  serviceName: '',
  unitPrice: '',
  quantity: '1',
};

// In-progress service selections are drafted to localStorage per visit so
// navigating away mid-visit (e.g. "Open chart") doesn't lose the work.
// Cleared once invoices are generated.
interface WorkspaceDraft {
  sharedItems: WorkItem[];
  checkedByItem: string[][];
  extrasByHorse: Record<string, WorkItem[]>;
  draft: typeof emptyDraft;
  extraDraftHorse: string;
}

const workspaceDraftKey = (visitGroupId: string) => `barn-visit-draft:${visitGroupId}`;

const readWorkspaceDraft = (visitGroupId: string): WorkspaceDraft | null => {
  try {
    const raw = localStorage.getItem(workspaceDraftKey(visitGroupId));
    return raw ? (JSON.parse(raw) as WorkspaceDraft) : null;
  } catch {
    return null;
  }
};

const AlertChips: FC<{ horse: BarnRosterHorse }> = ({ horse }) => {
  const alerts = Array.isArray(horse.medicalAlerts) ? horse.medicalAlerts : [];
  if (alerts.length === 0) return null;
  return (
    <Box display="inline-flex" gap={0.5}>
      {alerts.slice(0, 3).map((alert) => (
        <Tooltip key={alert.id} title={alert.text}>
          <Chip
            label={alert.isCritical ? 'Critical' : 'Alert'}
            size="small"
            color={alert.isCritical ? 'error' : 'warning'}
            variant={alert.isCritical ? 'filled' : 'outlined'}
          />
        </Tooltip>
      ))}
      {alerts.length > 3 && <Chip label={`+${alerts.length - 3}`} size="small" variant="outlined" />}
    </Box>
  );
};

/**
 * Barn visit workspace: Treat & document → Select services (matrix) →
 * Review & confirm (per-client estimates) → one barn-operations submit with
 * perHorseItems + visitGroupId + consolidateByClient. Online-only (invoices).
 */
const BarnVisitWorkspace: FC = () => {
  const { visitGroupId = '' } = useParams<{ visitGroupId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isOffline = useIsOffline();
  const formatInstant = useFormatInstant();

  // ── Visit + roster data ────────────────────────────────────────────────
  const { data: appointments = [], isLoading: loadingVisit } = useQuery(
    ['appointments', { visitGroupId }],
    () => appointmentApi.list({ visitGroupId }),
    { enabled: !!visitGroupId && !isOffline },
  );

  const barnId = appointments[0]?.barnId ?? null;
  const { data: barn } = useQuery(
    ['barn', barnId],
    () => barnsService.get(barnId!),
    { enabled: !!barnId && !isOffline },
  );

  const visitDate = appointments[0]?.date;
  const rosterByHorse = useMemo(() => {
    const map = new Map<string, BarnRosterHorse>();
    for (const horse of barn?.horses ?? []) map.set(horse.id, horse);
    return map;
  }, [barn]);

  // The visit's horses, in appointment order (roster entry may be missing if
  // a horse was moved after scheduling — fall back to the appointment's horse).
  // The filter narrows the now-nullable horseId: visit-group appointments are
  // always per-horse (barn-level bookings have no visitGroupId, so they never
  // load into this workspace).
  const visitHorses = useMemo(
    () =>
      appointments
        .filter((appt): appt is AppointmentListItem & { horseId: string } => !!appt.horseId)
        .map((appt) => ({
          appointment: appt,
          horseId: appt.horseId,
          name: appt.horse?.name ?? rosterByHorse.get(appt.horseId)?.name ?? 'Unknown',
          roster: rosterByHorse.get(appt.horseId),
        })),
    [appointments, rosterByHorse],
  );

  // ── Treat & document dialogs ───────────────────────────────────────────
  // Two unrelated server gates on one screen (INV-108): documenting is manage:medical_records
  // (medical-records / soap-notes controllers) and the invoice submit is manage:charges
  // (billing.controller.ts barnOperations). A vet who documents a barn round and never bills, and
  // a bookkeeper who bills and never documents, are both real people here.
  const rbac = useRBAC();
  const can = barnVisitControls(rbac);

  const [noteHorseId, setNoteHorseId] = useState<string | null>(null);
  const [soapHorse, setSoapHorse] = useState<{ horseId: string; name: string; appointmentId: string } | null>(null);

  const createVisitNote = useMutation(
    (data: { horseId: string; type: string; date: string; notes: string; tags: string[] }) =>
      medicalRecordsApi.create(data),
    {
      onSuccess: (_r, vars) => {
        queryClient.invalidateQueries(['horse-records', vars.horseId]);
        enqueueSnackbar('Visit note saved', { variant: 'success' });
      },
      onError: () => {
        enqueueSnackbar('Failed to save visit note', { variant: 'error' });
      },
    },
  );

  // ── Select services (matrix) ───────────────────────────────────────────
  const [restoredDraft] = useState<WorkspaceDraft | null>(() =>
    visitGroupId ? readWorkspaceDraft(visitGroupId) : null,
  );
  const [sharedItems, setSharedItems] = useState<WorkItem[]>(restoredDraft?.sharedItems ?? []);
  // checkedByItem[i] = horses receiving shared item i (default: all)
  const [checkedByItem, setCheckedByItem] = useState<Array<Set<string>>>(
    (restoredDraft?.checkedByItem ?? []).map((ids) => new Set(ids)),
  );
  const [extrasByHorse, setExtrasByHorse] = useState<Map<string, WorkItem[]>>(
    new Map(Object.entries(restoredDraft?.extrasByHorse ?? {})),
  );
  const [draft, setDraft] = useState(restoredDraft?.draft ?? emptyDraft);
  const [extraDraftHorse, setExtraDraftHorse] = useState<string>(
    restoredDraft?.extraDraftHorse ?? '',
  );

  // Draft the matrix on every change; drop the draft when the slate is blank.
  useEffect(() => {
    if (!visitGroupId) return;
    const hasWork =
      sharedItems.length > 0 || extrasByHorse.size > 0 || draft.serviceName.trim() !== '';
    try {
      if (!hasWork) {
        localStorage.removeItem(workspaceDraftKey(visitGroupId));
        return;
      }
      const toSave: WorkspaceDraft = {
        sharedItems,
        checkedByItem: checkedByItem.map((s) => Array.from(s)),
        extrasByHorse: Object.fromEntries(extrasByHorse),
        draft,
        extraDraftHorse,
      };
      localStorage.setItem(workspaceDraftKey(visitGroupId), JSON.stringify(toSave));
    } catch {
      /* storage unavailable — drafts are best-effort */
    }
  }, [visitGroupId, sharedItems, checkedByItem, extrasByHorse, draft, extraDraftHorse]);

  const { data: catalogItems = [] } = useQuery<ServiceCatalogItem[]>(
    ['service-catalog', draft.serviceType],
    () => serviceCatalogService.getAll({ category: draft.serviceType, isActive: true }),
    { enabled: !isOffline },
  );

  const draftToItem = (): WorkItem | null => {
    const name = draft.serviceName.trim();
    if (!name) return null;
    const catalog = catalogItems.find((c) => c.name === name);
    const price = draft.unitPrice !== '' ? Number(draft.unitPrice) : catalog?.basePrice ?? 0;
    // Quantity is a medication-only concept end-to-end: the submit payload
    // strips it for other types and the backend bills qty 1 — so the estimate
    // and matrix must agree. A stale qty from a pre-type-switch draft would
    // otherwise show "×3" and estimate 3× while the invoice bills 1×.
    // Hundredths (ADR-0038): 0.53 bills 0.53; an empty or zero box bills one, as before.
    const typed = parseQuantity(draft.quantity);
    const quantity = draft.serviceType === 'medication' && typed !== null && typed > 0 ? typed : 1;
    return { serviceType: draft.serviceType, serviceName: name, unitPrice: price, quantity };
  };

  const addSharedItem = () => {
    const item = draftToItem();
    if (!item) return;
    setSharedItems((prev) => [...prev, item]);
    setCheckedByItem((prev) => [...prev, new Set(visitHorses.map((v) => v.horseId))]);
    setDraft(emptyDraft);
  };

  const addExtraItem = () => {
    const item = draftToItem();
    if (!item || !extraDraftHorse) return;
    setExtrasByHorse((prev) => {
      const next = new Map(prev);
      next.set(extraDraftHorse, [...(next.get(extraDraftHorse) ?? []), item]);
      return next;
    });
    setDraft(emptyDraft);
  };

  const toggleCell = (itemIndex: number, horseId: string) =>
    setCheckedByItem((prev) => {
      const next = prev.map((s) => new Set(s));
      if (next[itemIndex].has(horseId)) next[itemIndex].delete(horseId);
      else next[itemIndex].add(horseId);
      return next;
    });

  // Materialized per-horse work list (shared-where-checked + extras).
  const perHorseItems = useMemo(() => {
    return visitHorses
      .map(({ horseId }) => {
        const items: WorkItem[] = [];
        sharedItems.forEach((item, i) => {
          if (checkedByItem[i]?.has(horseId)) items.push(item);
        });
        items.push(...(extrasByHorse.get(horseId) ?? []));
        return { horseId, items };
      })
      .filter((entry) => entry.items.length > 0);
  }, [visitHorses, sharedItems, checkedByItem, extrasByHorse]);

  // ── Review & confirm: per-client estimates from ownership links ───────
  const { clientEstimates, unassignedTotal } = useMemo(() => {
    const byClient = new Map<string, ClientEstimate>();
    let unassigned = 0;

    for (const entry of perHorseItems) {
      const subtotal = entry.items.reduce(
        (sum, item) => sum + roundDollars(item.unitPrice * (item.quantity ?? 1)),
        0,
      );
      const roster = rosterByHorse.get(entry.horseId);
      const links = roster?.ownerLinks ?? [];
      if (links.length === 0) {
        unassigned += subtotal;
        continue;
      }
      const rawShares = links.map((l) => l.ownershipPercentage ?? 100 / links.length);
      const shareSum = rawShares.reduce((s, p) => s + p, 0) || 1;
      links.forEach((link, i) => {
        const share = (rawShares[i] / shareSum) * subtotal;
        if (!byClient.has(link.clientId)) {
          byClient.set(link.clientId, {
            clientId: link.clientId,
            name: link.name,
            horseNames: [],
            estimated: 0,
          });
        }
        const est = byClient.get(link.clientId)!;
        est.estimated += share;
        if (roster && !est.horseNames.includes(roster.name)) est.horseNames.push(roster.name);
      });
    }

    return {
      clientEstimates: Array.from(byClient.values()).sort((a, b) => a.name.localeCompare(b.name)),
      unassignedTotal: unassigned,
    };
  }, [perHorseItems, rosterByHorse]);

  // ── Submit ─────────────────────────────────────────────────────────────
  const [results, setResults] = useState<any | null>(null);
  const submitMutation = useMutation(
    () =>
      api
        .post('/billing/barn-operations', {
          perHorseItems: perHorseItems.map((entry) => ({
            horseId: entry.horseId,
            items: entry.items.map(({ serviceType, serviceName, unitPrice, quantity, notes }) => ({
              serviceType,
              serviceName,
              unitPrice,
              ...(serviceType === 'medication' && quantity ? { quantity } : {}),
              ...(notes ? { notes } : {}),
            })),
          })),
          // The barn-visit service DAY — BOTH halves of it, because the two
          // date branches fixed different ends of the same line.
          //
          // WHICH DAY (`fix/dates-scheduling`): `Appointment.date` is an
          // INSTANT (DATE-KINDS §1.3 "Scheduling"), and `.slice(0, 10)` took
          // its UTC day, which for any evening visit in a US zone is TOMORROW.
          // The day a visit happened is the CLINIC's day of that moment, so it
          // is named in the clinic zone. (`formatInstant` returns '' for an
          // unparseable value — fall back to today rather than posting an
          // empty day.)
          //
          // WHICH ANCHOR (`fix/dates-soap-procedures`): `/billing/barn-operations`
          // does `new Date(dto.date)` and writes the result straight into
          // `Procedure.date`, `Vaccination.dateAdministered`,
          // `Medication.startDate`, `MedicalRecord.date` and
          // `Invoice.serviceDate` (`billing.service.ts:3208,3271,3284,3337,3420`),
          // so a bare `yyyy-MM-dd` lands on UTC MIDNIGHT — the anchor that reads
          // back as the previous evening in every US zone (W1/W2, DATE-KINDS
          // §2.5). Noon UTC is the sanctioned anchor, and it is what the OTHER
          // caller of this endpoint (`Billing/BarnOperations.tsx:493`) already
          // posts; the two must not disagree.
          //
          // Neither half is sufficient alone: the clinic day on a midnight
          // anchor still reads back a day early, and the noon anchor on the UTC
          // date part still anchors the WRONG day.
          date: ymdToNoonUtcIso(
            (visitDate && formatInstant(visitDate, 'yyyy-MM-dd')) || todayLocalISO(),
          ),
          performedBy: appointments[0]?.assignedVet?.name,
          createInvoices: true,
          visitGroupId,
          consolidateByClient: true,
        })
        .then((r) => r.data),
    {
      onSuccess: (data) => {
        setResults(data);
        // The visit is now invoiced — restoring the draft later would risk
        // generating (and billing) the same services twice.
        try {
          localStorage.removeItem(workspaceDraftKey(visitGroupId));
        } catch {
          /* best-effort */
        }
        queryClient.invalidateQueries(['appointments']);
        queryClient.invalidateQueries(['invoices']);
        // The Invoicing page's header tiles cache the full ClinicInvoiceStats
        // under ['billing-stats'] with staleTime 60s, which the 'invoices'
        // prefix does NOT match — so without this the clinic saw the new rows
        // (and the Dashboard's AR tile, which IS under that prefix) move while
        // Outstanding / Past due stayed put, across a fresh /billing navigation.
        queryClient.invalidateQueries(['billing-stats']);
        enqueueSnackbar(
          `Created ${data.summary.totalInvoices} invoice(s)` +
            (data.summary.totalStatements !== undefined
              ? ` and ${data.summary.totalStatements} client statement(s)`
              : ''),
          { variant: 'success' },
        );
      },
      onError: (err: any) => {
        enqueueSnackbar(err?.response?.data?.message ?? 'Failed to generate invoices', {
          variant: 'error',
        });
      },
    },
  );

  const alreadyInvoiced = appointments.length > 0 && appointments.every((a) => a.invoice);

  if (isOffline) {
    return (
      <Box sx={{ p: { xs: 1, sm: 1.5 } }}>
        <OfflineNotice>
          The barn-visit workspace needs a connection (it creates invoices).
        </OfflineNotice>
      </Box>
    );
  }

  if (loadingVisit) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (appointments.length === 0) {
    return (
      <Box sx={{ p: { xs: 1, sm: 1.5 } }}>
        <Alert severity="warning">Barn visit not found (it may have been cancelled).</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'hidden', p: { xs: 1, sm: 1.5 } }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => (barnId ? navigate(`/barns/${barnId}`) : navigate('/barns'))}
        sx={{ mb: 1 }}
      >
        {barn?.name ?? 'Barn'}
      </Button>

      <Box mb={1.5}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.125rem' } }}>
          Barn visit · {appointments[0].location}
        </Typography>
        <Typography
          color="textSecondary"
          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          data-testid="barn-visit-datetime"
        >
          {/* INSTANT (Appointment.date) — the clinic's wall clock, not the browser's. */}
          {visitDate ? formatInstant(visitDate, 'EEEE, MMM d yyyy · h:mm a') : ''} ·{' '}
          {visitHorses.length} horse{visitHorses.length === 1 ? '' : 's'}
          {appointments[0].assignedVet?.name ? ` · ${appointments[0].assignedVet.name}` : ''}
        </Typography>
      </Box>

      {alreadyInvoiced && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Every appointment on this visit is already invoiced.
        </Alert>
      )}

      {/* ── 1 · Treat & document ─────────────────────────────────────── */}
      <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0.5 }}>
        1 · Treat & document
      </Typography>
      <Box mb={2}>
        {visitHorses.map(({ horseId, name, roster, appointment }) => (
          <Accordion key={horseId} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography fontWeight={600}>{name}</Typography>
                {roster && <AlertChips horse={roster} />}
                {appointment.invoice && (
                  <Chip label={`Invoiced · ${appointment.invoice.invoiceNumber}`} size="small" />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" gap={1} flexWrap="wrap">
                {can.quickNote && (
                  <Button
                    size="small"
                    startIcon={<NoteAddIcon />}
                    onClick={() => setNoteHorseId(horseId)}
                  >
                    Quick note
                  </Button>
                )}
                {can.startSoap && (
                  <Button
                    size="small"
                    startIcon={<SoapIcon />}
                    onClick={() => setSoapHorse({ horseId, name, appointmentId: appointment.id })}
                  >
                    Start SOAP
                  </Button>
                )}
                {/* A link into the patient chart, whose own route is `view:patients`-gated — so
                    without the slug this offered a one-way trip to a "no access" sentence. */}
                {can.openPatientChart && (
                  <Button size="small" onClick={() => navigate(`/patients/${horseId}`)}>
                    Open chart
                  </Button>
                )}
              </Box>
              {roster && roster.ownerLinks.length > 0 && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  Owners:{' '}
                  {roster.ownerLinks
                    .map(
                      (link) =>
                        `${link.name}${link.ownershipPercentage != null ? ` (${link.ownershipPercentage}%)` : ''}`,
                    )
                    .join(', ')}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* ── 2 · Select services ──────────────────────────────────────── */}
      <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0.5 }}>
        2 · Select services
      </Typography>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <TextField
            select
            size="small"
            label="Type"
            value={draft.serviceType}
            onChange={(e) =>
              setDraft({ ...draft, serviceType: e.target.value as ServiceType, serviceName: '', unitPrice: '' })
            }
            sx={{ width: 140 }}
          >
            <MenuItem value="vaccination">Vaccination</MenuItem>
            <MenuItem value="procedure">Procedure</MenuItem>
            <MenuItem value="medication">Medication</MenuItem>
          </TextField>
          <Autocomplete
            freeSolo
            size="small"
            options={catalogItems.map((c) => c.name)}
            inputValue={draft.serviceName}
            onInputChange={(_e, value) => {
              const catalog = catalogItems.find((c) => c.name === value);
              setDraft((d) => ({
                ...d,
                serviceName: value,
                unitPrice: catalog ? String(catalog.basePrice) : d.unitPrice,
              }));
            }}
            sx={{ minWidth: 240, flex: 1 }}
            renderInput={(params) => <TextField {...params} label="Service" />}
          />
          <TextField
            size="small"
            label="Price ($)"
            type="number"
            value={draft.unitPrice}
            onChange={(e) => setDraft({ ...draft, unitPrice: e.target.value })}
            sx={{ width: 110 }}
            inputProps={{ min: 0, step: 0.01 }}
          />
          {draft.serviceType === 'medication' && (
            <QuantityField
              size="small"
              label="Qty"
              value={parseQuantity(draft.quantity)}
              onChange={(next) => setDraft({ ...draft, quantity: next === null ? '' : String(next) })}
              sx={{ width: 80 }}
            />
          )}
          <Button startIcon={<AddIcon />} onClick={addSharedItem} disabled={!draft.serviceName.trim()}>
            All horses
          </Button>
          <TextField
            select
            size="small"
            label="…or one horse"
            value={extraDraftHorse}
            onChange={(e) => setExtraDraftHorse(e.target.value)}
            sx={{ width: 170 }}
          >
            {visitHorses.map((v) => (
              <MenuItem key={v.horseId} value={v.horseId}>
                {v.name}
              </MenuItem>
            ))}
          </TextField>
          <Button
            startIcon={<AddIcon />}
            onClick={addExtraItem}
            disabled={!draft.serviceName.trim() || !extraDraftHorse}
          >
            Add
          </Button>
        </Box>

        {(sharedItems.length > 0 || extrasByHorse.size > 0) && (
          <TableContainer sx={{ mt: 1.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Price</TableCell>
                  {visitHorses.map((v) => (
                    <TableCell key={v.horseId} align="center">
                      {v.name}
                    </TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {sharedItems.map((item, i) => (
                  <TableRow key={`shared-${i}`}>
                    <TableCell>
                      {item.serviceName}{' '}
                      <Typography component="span" variant="caption" color="textSecondary">
                        ({item.serviceType}
                        {item.quantity != null && item.quantity !== 1 ? ` ×${formatQuantity(item.quantity)}` : ''})
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatDollars(item.unitPrice)}</TableCell>
                    {visitHorses.map((v) => (
                      <TableCell key={v.horseId} align="center" padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={checkedByItem[i]?.has(v.horseId) ?? false}
                          onChange={() => toggleCell(i, v.horseId)}
                        />
                      </TableCell>
                    ))}
                    <TableCell padding="checkbox">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSharedItems((prev) => prev.filter((_x, idx) => idx !== i));
                          setCheckedByItem((prev) => prev.filter((_x, idx) => idx !== i));
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {Array.from(extrasByHorse.entries()).flatMap(([horseId, items]) =>
                  items.map((item, j) => (
                    <TableRow key={`extra-${horseId}-${j}`}>
                      <TableCell>
                        {item.serviceName}{' '}
                        <Typography component="span" variant="caption" color="textSecondary">
                          ({item.serviceType} — only{' '}
                          {visitHorses.find((v) => v.horseId === horseId)?.name})
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatDollars(item.unitPrice)}</TableCell>
                      {visitHorses.map((v) => (
                        <TableCell key={v.horseId} align="center" padding="checkbox">
                          <Checkbox size="small" checked={v.horseId === horseId} disabled />
                        </TableCell>
                      ))}
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setExtrasByHorse((prev) => {
                              const next = new Map(prev);
                              const remaining = (next.get(horseId) ?? []).filter((_x, idx) => idx !== j);
                              if (remaining.length) next.set(horseId, remaining);
                              else next.delete(horseId);
                              return next;
                            })
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )),
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ── 3 · Review & confirm ─────────────────────────────────────── */}
      <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0.5 }}>
        3 · Review & confirm
      </Typography>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        {perHorseItems.length === 0 ? (
          <Typography color="textSecondary">Add services above to see the billing preview.</Typography>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell>Horses</TableCell>
                    <TableCell align="right">Estimated (pre-tax)</TableCell>
                    <TableCell align="right">Statements</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientEstimates.map((est) => (
                    <TableRow key={est.clientId}>
                      <TableCell>{est.name}</TableCell>
                      <TableCell>{est.horseNames.join(', ')}</TableCell>
                      <TableCell align="right">{formatDollars(est.estimated)}</TableCell>
                      <TableCell align="right">1</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {unassignedTotal > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {formatDollars(unassignedTotal)} belongs to horses with no linked owner — those
                invoices are created per horse but can't ride a client statement.
              </Alert>
            )}
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              Estimates split co-owned horses by ownership share; final invoices add clinic tax.
              Co-owned charges are billed as separate per-owner invoices, then each client gets
              one consolidated statement.
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            {can.submit && (
              <Button
                variant="contained"
                startIcon={<ReceiptIcon />}
                onClick={() => submitMutation.mutate()}
                disabled={submitMutation.isLoading || !!results}
              >
                Generate invoices & statements
              </Button>
            )}
          </>
        )}
      </Paper>

      {/* ── Results ──────────────────────────────────────────────────── */}
      {results && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', mb: 1 }}>
            Done — {results.summary.totalInvoices} invoice(s)
            {results.summary.totalStatements !== undefined &&
              `, ${results.summary.totalStatements} statement(s)`}
          </Typography>
          {(results.clientInvoices ?? []).map((ci: any) => (
            <Box key={ci.clientId} display="flex" gap={1} alignItems="center" mb={0.5}>
              {ci.status === 'success' ? (
                <>
                  <Chip label={ci.invoiceNumber} size="small" color="success" variant="outlined" />
                  <Typography variant="body2">
                    {ci.clientName} — {formatDollars(ci.totalAmount)}
                  </Typography>
                  <Link component="button" variant="body2" onClick={() => navigate('/billing')}>
                    View in Invoicing
                  </Link>
                </>
              ) : (
                <Alert severity="error" sx={{ py: 0, width: '100%' }}>
                  {ci.clientName}: {ci.error} (per-horse invoices were still created)
                </Alert>
              )}
            </Box>
          ))}
          <TableContainer sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Horse</TableCell>
                  <TableCell>Invoices</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.results.map((r: any) => (
                  <TableRow key={r.horseId}>
                    <TableCell>{r.horseName}</TableCell>
                    <TableCell>{r.invoiceNumbers.join(', ') || '—'}</TableCell>
                    <TableCell align="right">{formatDollars(r.amount)}</TableCell>
                    <TableCell>
                      {r.status === 'success' ? (
                        <Chip label="Success" size="small" color="success" variant="outlined" />
                      ) : (
                        <Tooltip title={r.error ?? ''}>
                          <Chip label="Error" size="small" color="error" />
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── Dialogs ──────────────────────────────────────────────────── */}
      {noteHorseId && (
        <VisitNoteDialog
          open={!!noteHorseId}
          onClose={() => setNoteHorseId(null)}
          horseId={noteHorseId}
          onSave={(data) => {
            createVisitNote.mutate(data);
            setNoteHorseId(null);
          }}
        />
      )}
      {soapHorse && (
        <SoapNoteDialog
          open={!!soapHorse}
          onClose={() => setSoapHorse(null)}
          horseId={soapHorse.horseId}
          horseName={soapHorse.name}
          appointmentId={soapHorse.appointmentId}
          initialDate={visitDate}
        />
      )}
    </Box>
  );
};

export default BarnVisitWorkspace;
